<template>
  <div
    class="w-full h-full flex flex-col bg-white border border-gray-50 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300 relative overflow-hidden group rounded-2xl"
  >
    <NuxtLink class="flex w-full h-full flex-col" :to="resolvedLink">
      <!-- Image & Video -->
      <div v-if="mediaItems.length || color" class="w-full flex relative">
        <client-only>
          <!-- Media Slider -->
          <Swiper
            v-if="mediaItems.length > 1"
            class="w-full product-list-slider"
            :loop="true"
            :modules="swiperModules"
            :pagination="paginationConfig"
          >
            <SwiperSlide
              v-for="(item, index) in mediaItems"
              :key="index"
              class="select-none"
            >
              <AtomsImage
                v-if="item?.mime?.includes('image')"
                class="w-full"
                :class="mediaClass"
                :media="item"
                :alt="item?.alternativeText ?? title ?? item?.hash ?? item?.name"
                sizeHint="quarter"
              />
              <AtomsVideo
                v-else-if="item?.mime?.includes('video')"
                :src="item?.formats?.large?.url ?? item?.url"
                :type="item?.mime"
                :autoplay="true"
                class="w-full"
                :class="mediaClass"
              />
            </SwiperSlide>
          </Swiper>
          <!-- Media Slider -->

          <!-- Single Media -->
          <AtomsImage
            v-else-if="mediaItems.length === 1"
            class="w-full"
            :class="mediaClass"
            :media="mediaItems[0]"
            :alt="mediaItems[0]?.alternativeText ?? title ?? mediaItems[0]?.hash ?? mediaItems[0]?.name"
            sizeHint="quarter"
          />

          <!-- Color Fallback -->
          <div
            v-else
            class="w-full"
            :class="aspectClass"
            :style="{ backgroundColor: color }"
          ></div>
        </client-only>

        <div
          v-if="badge"
          class="absolute left-0 top-0 bg-[#e30613] text-white text-sm font-bold px-3 py-1.5 z-10 rounded-br-xl"
        >
          {{ badge }}
        </div>
      </div>
      <!-- Image & Video -->

      <!-- Text -->
      <div
        v-if="title || subtitle || hasPrice"
        class="w-full flex-1 flex flex-col p-4 md:p-6 lg:p-8 gap-1"
      >
        <!-- Title -->
        <h3
          v-if="title"
          class="w-full font-bold text-[15px] md:text-[16px] uppercase tracking-wider text-gray-900 leading-snug line-clamp-2"
          v-html="title"
        ></h3>
        <!-- Title -->

        <!-- Subtitle (SKU / Code) -->
        <div
          v-if="subtitle"
          class="w-full mt-1.5 text-[12px] md:text-[13px] text-gray-500 uppercase tracking-wider line-clamp-1"
        >
          {{ subtitle }}
        </div>

        <!-- Price -->
        <div
          v-if="hasPrice"
          class="w-full flex gap-3 md:gap-5 justify-between items-center mt-auto pt-4"
        >
          <span v-if="formattedPrice" class="font-bold text-[15px] text-brand-first">
            {{ formattedPrice }}
          </span>
          <span
            v-if="formattedOldPrice"
            class="font-normal text-[14px] line-through opacity-40 text-gray-500"
          >
            {{ formattedOldPrice }}
          </span>
        </div>
        <!-- Price -->
      </div>
      <!-- Text -->
    </NuxtLink>
  </div>
</template>
<script setup>
import { computed, ref } from "vue";
import { useSiteSettingsStore } from "@/store/index";
const siteSettings = useSiteSettingsStore();
// import { Swiper, SwiperSlide } from "swiper/vue"; // Registered globally via plugins/swiper.client.js
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { useLocalePath } from "#i18n";
import { useI18n } from "vue-i18n";
const props = defineProps({
  title: {
    type: String,
  },
  subtitle: {
    type: String,
    default: "",
  },
  badge: {
    type: String,
    default: "",
  },
  price: {
    type: [Number, String],
    default: null,
  },
  oldPrice: {
    type: [Number, String],
    default: null,
  },
  currency: { type: String, default: "TRY" },
  link: { type: [String, Object] },
  media: {
    type: Array,
    default: () => [],
  },
  mediaFilled: { type: Boolean, default: false },
  position: { type: String, default: "left" },
  mediaSize: { type: String },
  objectFit: { type: String, default: "cover" },
  rating: { type: [Number, String], default: null },
  color: { type: String, default: "" },
});

const { locale } = useI18n();
const localePath = useLocalePath();
const resolvedLink = computed(() => {
  if (!props.link) {
    return null;
  }
  if (typeof props.link === "string") {
    if (props.link.startsWith("http") || props.link.startsWith("/")) {
      return props.link;
    }
    return localePath(props.link);
  }
  return localePath(props.link);
});

const mediaItems = computed(() => props.media || []);

const paginationConfig = computed(() => ({
  clickable: true,
  dynamicBullets: false,
}));

const swiperModules = [Pagination];

const rating = ref(props.rating);

const formatCurrency = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return String(value);
  }
  try {
    return new Intl.NumberFormat(locale.value === "tr" ? "tr-TR" : undefined, {
      style: "currency",
      currency: props.currency || "TRY",
      maximumFractionDigits: 0,
    }).format(numericValue);
  } catch (_err) {
    return `${numericValue} ${props.currency || ""}`.trim();
  }
};

const formattedPrice = computed(() => formatCurrency(props.price));
const formattedOldPrice = computed(() => formatCurrency(props.oldPrice));
const hasPrice = computed(() =>
  Boolean(formattedPrice.value || formattedOldPrice.value),
);
const aspectClass = computed(() => ({
  'aspect-square': props.mediaSize === 'aspect-ratio-1/1',
  'aspect-[3/2]': props.mediaSize === 'aspect-ratio-3/2',
  'aspect-[3/4]': props.mediaSize === 'aspect-ratio-3/4',
  'aspect-[4/3]': props.mediaSize === 'aspect-ratio-4/3',
  'aspect-[4/5]': props.mediaSize === 'aspect-ratio-4/5',
  'aspect-video': !props.mediaSize || props.mediaSize === 'aspect-ratio-16/9',
}));
const mediaClass = computed(() => [
  aspectClass.value,
  {
    'object-contain': props.mediaFilled === true,
    'object-cover object-center': props.mediaFilled !== true,
  },
]);
const textContainerClass = computed(() => ({
  'items-center': props.position === 'center',
  'items-end': props.position === 'right',
}));
const textAlignmentClass = computed(() => [
  props.position === 'center' ? 'text-center' : props.position === 'right' ? ' text-right' : 'text-left',
]);
</script>
